import type { UID } from 'agora-rtc-sdk-ng'
import type {
  PlayerError,
  PlayerEventType,
  PlayerState,
  Quality,
} from 'amazon-ivs-player'
import type { StoreApi } from 'zustand'

import type {
  Api_LiveStream,
  Api_LiveStream_Event,
  Api_LiveStream_Flash_Deal_Listing,
  Api_LiveStream_Message,
  Api_LiveStream_Message_Rate_Limit,
  Api_LiveStream_Running_Announcement,
  Api_Product,
  Api_Video,
  Api_Video_Promotion,
} from '@core/api'
import type { IPlayerParams } from '@core/types'
import type { GetState, NamedSet } from '@core/zustand'
import type { IAppContext } from '@embed/components/helpers'
import type { AnyWidgetAppContext } from '@embed/hooks/useEmbedAppContext'
import type { IGuestSpeakerSlice } from '@player/features/guestSpeaker/model'
import type { createSlice as createPlayerModalSlice } from '@player/features/modal/modalRegistry'

import type { IStoreActions } from './actionTypes'

export enum ProductModalSource {
  SHOPPING_BAG = 'SHOPPING_BAG', // ShoppingBagButton and ProductList in Shopping wing
  PRODUCTS_PINNED = 'PRODUCTS_PINNED', // ProductCarousel, ProductHighlight, and ProductButton
}

export enum ILiveStreamState {
  ACTIVE_WAITING = 'ACTIVE_WAITING',
  ACTIVE_PLAYING = 'ACTIVE_PLAYING',
  ACTIVE_PAUSED = 'ACTIVE_PAUSED',
  REPLAY_PLAYING = 'REPLAY_PLAYING',
}

export enum ILiveStreamChatInputState {
  PUBLIC = 'PUBLIC',
  PRIVATE = 'PRIVATE',
}

export type ILiveStreamError = {
  type: string // 'amazon-ivs-player' ErrorType
  message: string
}

export type ILiveStreamActions = {
  postMessage: (props: {
    text: string
    username?: string
    isPrivate?: boolean
  }) => void
  changeUsername: (newName: string) => void
  sendHeart: (props?: { sendWSMessage: boolean }) => void
  trackShare: () => void
  trackAddToCart: () => void
  trackViewingProduct: (productId: string, productUnitId?: string) => void
  readyToStream: (invitationToken: string, uid: UID) => void
  unreadyToStream: (invitationToken: string) => void
  endGuestStream(): void
  updateSubtitleLocale: (locale: string | null) => void
}

/** A single product's view count within the backend's trailing FOMO window. */
export interface IFomoProductViewingCount {
  count: number
  product_id: string
}

/** Latest `fomo_viewing_product_counts` broadcast received for a live stream. */
export interface IFomoProductViewingCounts {
  counts: IFomoProductViewingCount[]
  /** Epoch ms when the payload was received, used by the FE staleness guard. */
  receivedAt: number
}

export interface ICorePlayerContext {
  actions: IStoreActions
  appContext?: IAppContext | AnyWidgetAppContext
  currentVideoAdId: string | null
  deals: { [liveStreamId: string]: Api_LiveStream_Flash_Deal_Listing[] }
  expandedProduct: Api_Product | null
  flowLinkId: string
  hasAutoplay: boolean
  hasHydratedProducts: { [videoId: string]: boolean }
  isCartRevealed: boolean
  productAddedToCart: boolean
  isIMAActive: boolean
  isInteractable: boolean
  activeInteractionIds: string[]
  visibleInteractionIds: string[]
  // transcript is a reserved value for the transcript modal
  expandedInteractionId: 'transcript' | string | null
  isProductDescriptionExpanded: boolean
  isProductModalExpanded: ProductModalSource | null
  isQRModeEnabled: boolean
  isTapToUnmuteVisible: boolean
  engagedVideoId: string | null
  engagedVideoIdV2: string | null

  /**
   * This is the actual mute status of the video element
   */
  isVideoMuted: boolean

  /**
   * Whether the video is visible in the viewport (used to determine if the video is
   * the main one in vertical scroll container)
   */
  isVisible: boolean

  /**
   * IVS current low latency setting, defaults to true unless feature flag is set to false
   */
  ivsLowLatencyEnabled: boolean
  /**
   * Current desired IVS Quality user setting, defaults to undefined = `auto`
   */
  ivsQuality: Quality | undefined
  ivsState: {
    playerState: PlayerState | null
    lastPlayerEvent: PlayerEventType | null
    lastPlayerError: PlayerError | null
  }
  liveStreamActions: { [liveStreamId: string]: ILiveStreamActions }
  liveStreamBotGreeting: { [liveStreamId: string]: boolean }
  liveStreamError: ILiveStreamError | null

  /**
   * WebSocket error message
   */
  liveStreamWSError: string | null
  liveStreamEvents: { [liveStreamId: string]: Api_LiveStream_Event[] }
  liveStreamMessages: { [liveStreamId: string]: Api_LiveStream_Message[] }
  liveStreamPinnedMessages: {
    [liveStreamId: string]: Api_LiveStream_Message[]
  }
  liveStreamProducts: { [liveStreamId: string]: Api_Product[] }
  videoPromotions: { [liveStreamId: string]: Api_Video_Promotion[] }
  fomoProductViewingCounts: {
    [liveStreamId: string]: IFomoProductViewingCounts
  }
  liveStreamRunningAnnouncements: {
    [liveStreamId: string]: Api_LiveStream_Running_Announcement
  }
  liveStreamState: ILiveStreamState | null
  liveStreamTemporaryChatHighlightMessage: {
    [liveStreamId: string]: Api_LiveStream_Message
  }
  liveStreamChatInputState: ILiveStreamChatInputState
  liveStreams: { [liveStreamId: string]: Partial<Api_LiveStream> }
  nextVideoAdId: string | null
  params?: IPlayerParams
  showGiveawayAnimation: boolean
  usernameChangeRequest: {
    ref: string
    status: null | string
    username: string
    message?: string
  }
  // Current video being played (note: this might not be the same as initialVideo
  // if the video has FWAi pre-roll or user entered interactive journey)
  video: Api_Video | null
  videoAds: { [key: string]: Api_Video }
  videoAdsAll: { [key: string]: Api_Video }
  videoId: string
  videoOverlaysVisible: boolean
  /** Mobile livestream swipe-to-hide: true when overlay is hidden. */
  liveOverlayHidden: boolean
  /** True while the first-run hint nudges the overlay to demo the gesture. */
  liveOverlayNudging: boolean
  videoProducts: { [key: string]: Api_Product[] }
  viewerCountryCode: string
  liveStreamMessageRateLimit: {
    [liveStreamId: string]: Api_LiveStream_Message_Rate_Limit
  }
  isVideoEngaged: boolean
  isVideoEngagedV2: boolean
}

export type IPlayerContext = ICorePlayerContext &
  IGuestSpeakerSlice &
  ReturnType<typeof createPlayerModalSlice>

export type StateCreator = (
  set: NamedSet<IPlayerContext>,
  get: GetState<IPlayerContext>,
) => IPlayerContext

export type { IStoreActions } from './actionTypes'

export type TStoreApi = StoreApi<IPlayerContext>
